import {
  CASE_STATUS_VALUES,
  SCENARIO_ACTION_TYPE_VALUES,
  type ScenarioAction,
  type ScenarioActionType
} from '../app/types';
import type { GameState } from '../app/GameState';
import type { AudioManager } from './AudioManager';

export interface ScreenShakeRequest {
  intensity: number;
  duration: number;
}

export interface FadeRequest {
  direction: 'in' | 'out' | 'inOut';
  color: string;
  duration: number;
}

export interface ScenarioActionWarning {
  type: string;
  reason: string;
  action: unknown;
}

export interface ScenarioActionExecutionResult {
  type: string;
  executed: boolean;
  warning?: ScenarioActionWarning;
}

export interface ScenarioActionExecutionContext {
  gameState: GameState;
  audio?: Pick<AudioManager, 'playSe'>;
  onEvidenceAcquired?: (evidenceId: string) => void | Promise<void>;
  onScreenShake?: (request: ScreenShakeRequest) => void;
  onFade?: (request: FadeRequest) => void | Promise<void>;
  onGoToTitle?: () => void | Promise<void>;
  warn?: (warning: ScenarioActionWarning) => void;
}

type MaybePromise<T> = T | Promise<T>;
type ScenarioActionMap = {
  [ActionType in ScenarioActionType]: Extract<ScenarioAction, { type: ActionType }>;
};
type ScenarioActionHandler<ActionType extends ScenarioActionType> = (
  action: ScenarioActionMap[ActionType],
  context: ScenarioActionExecutionContext
) => MaybePromise<void>;
type AnyScenarioActionHandler = (
  action: ScenarioAction,
  context: ScenarioActionExecutionContext
) => MaybePromise<void>;

const DEFAULT_SCREEN_SHAKE: Readonly<ScreenShakeRequest> = {
  intensity: 0.5,
  duration: 350
};

const DEFAULT_FADE: Readonly<FadeRequest> = {
  direction: 'inOut',
  color: '#000000',
  duration: 500
};

const SUPPORTED_ACTION_TYPES: ReadonlySet<string> = new Set(SCENARIO_ACTION_TYPE_VALUES);
const SUPPORTED_CASE_STATUSES: ReadonlySet<string> = new Set(CASE_STATUS_VALUES);
const SUPPORTED_FADE_DIRECTIONS: ReadonlySet<string> = new Set(['in', 'out', 'inOut']);

export const SCENARIO_ACTION_HANDLERS: {
  [ActionType in ScenarioActionType]: ScenarioActionHandler<ActionType>;
} = {
  addEvidence: async (action, context) => {
    const alreadyAcquired = context.gameState.hasEvidence(action.id);
    context.gameState.addEvidence(action.id);
    if (!alreadyAcquired) {
      await context.onEvidenceAcquired?.(action.id);
    }
  },
  updateCaseStatus: (action, context) => {
    context.gameState.updateCaseStatus(action.id, action.status);
  },
  playSe: async (action, context) => {
    if (!context.audio) {
      throw new Error('AudioManager is not connected.');
    }

    await context.audio.playSe(action.id);
  },
  screenShake: (action, context) => {
    const settings = context.gameState.getSettings();
    if (!settings.screenShake || settings.reduceMotion) {
      return;
    }

    if (!context.onScreenShake) {
      throw new Error('screenShake callback is not connected.');
    }

    context.onScreenShake({
      intensity: normalizePositiveNumber(action.intensity, DEFAULT_SCREEN_SHAKE.intensity),
      duration: normalizePositiveNumber(action.duration, DEFAULT_SCREEN_SHAKE.duration)
    });
  },
  fade: async (action, context) => {
    if (!context.onFade) {
      throw new Error('fade callback is not connected.');
    }

    await context.onFade({
      direction: action.direction ?? DEFAULT_FADE.direction,
      color: action.color ?? DEFAULT_FADE.color,
      duration: normalizePositiveNumber(action.duration, DEFAULT_FADE.duration)
    });
  },
  goToTitle: async (_action, context) => {
    if (!context.onGoToTitle) {
      throw new Error('goToTitle callback is not connected.');
    }

    await context.onGoToTitle();
  }
};

export async function executeScenarioActions(
  actions: readonly unknown[] | undefined,
  context: ScenarioActionExecutionContext
): Promise<ScenarioActionExecutionResult[]> {
  if (!actions?.length) {
    return [];
  }

  const results: ScenarioActionExecutionResult[] = [];
  for (const action of actions) {
    results.push(await executeScenarioAction(action, context));
  }

  return results;
}

export async function executeScenarioAction(
  action: unknown,
  context: ScenarioActionExecutionContext
): Promise<ScenarioActionExecutionResult> {
  const type = getActionType(action);
  if (!type) {
    return warnAndSkip(context, {
      type: 'unknown',
      reason: 'Scenario action must be an object with a string type.',
      action
    });
  }

  if (!isScenarioActionType(type)) {
    return warnAndSkip(context, {
      type,
      reason: `Unsupported scenario action "${type}".`,
      action
    });
  }

  if (!isScenarioAction(action, type)) {
    return warnAndSkip(context, {
      type,
      reason: `Invalid payload for scenario action "${type}".`,
      action
    });
  }

  try {
    const handler = SCENARIO_ACTION_HANDLERS[type] as AnyScenarioActionHandler;
    await handler(action, context);
    return {
      type,
      executed: true
    };
  } catch (error) {
    return warnAndSkip(context, {
      type,
      reason: formatError(error),
      action
    });
  }
}

function getActionType(action: unknown): string | null {
  if (!isRecord(action) || typeof action.type !== 'string') {
    return null;
  }

  return action.type;
}

function isScenarioActionType(type: string): type is ScenarioActionType {
  return SUPPORTED_ACTION_TYPES.has(type);
}

function isScenarioAction(action: unknown, type: ScenarioActionType): action is ScenarioAction {
  if (!isRecord(action) || action.type !== type) {
    return false;
  }

  switch (type) {
    case 'addEvidence':
    case 'playSe':
      return isNonEmptyString(action.id);
    case 'updateCaseStatus':
      return isNonEmptyString(action.id) && isCaseStatus(action.status);
    case 'screenShake':
      return isOptionalFiniteNumber(action.intensity) && isOptionalFiniteNumber(action.duration);
    case 'fade':
      return (
        isOptionalFadeDirection(action.direction) &&
        isOptionalString(action.color) &&
        isOptionalFiniteNumber(action.duration)
      );
    case 'goToTitle':
      return true;
  }
}

function warnAndSkip(
  context: ScenarioActionExecutionContext,
  warning: ScenarioActionWarning
): ScenarioActionExecutionResult {
  if (context.warn) {
    context.warn(warning);
  } else {
    console.warn(`[ScenarioActions] ${warning.reason}`, warning.action);
  }

  return {
    type: warning.type,
    executed: false,
    warning
  };
}

function normalizePositiveNumber(value: number | undefined, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) {
    return fallback;
  }

  return value;
}

function isCaseStatus(value: unknown): boolean {
  return typeof value === 'string' && SUPPORTED_CASE_STATUSES.has(value);
}

function isOptionalFadeDirection(value: unknown): boolean {
  return value === undefined || (typeof value === 'string' && SUPPORTED_FADE_DIRECTIONS.has(value));
}

function isOptionalFiniteNumber(value: unknown): boolean {
  return value === undefined || (typeof value === 'number' && Number.isFinite(value));
}

function isOptionalString(value: unknown): boolean {
  return value === undefined || typeof value === 'string';
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function formatError(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
